import { getLanguage as getInterfaceLanguage, getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { LineChart as LineChartIcon } from 'lucide-react';
import { ViewsSeriesData } from './types';
import { 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart,
  Line,
  ReferenceLine
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ViewsChartProps {
  data: ViewsSeriesData[];
  isLoading: boolean;
}

// Tooltip Premium Apple-Like (PRD 10/10 Final - Sombra 0 4px 16px rgba(0,0,0,0.08))
const PremiumTooltip = ({ active, payload, label }: any) => {
  useLanguage();
  if (!active || !payload || !payload.length) return null;

  let formattedDate = label;
  try {
    formattedDate = format(parseISO(label), "dd MMM yyyy", { locale: getInterfaceDateLocale() });
  } catch (e) {
    // Keep original label if parsing fails
  }

  // Extrair métricas do payload com verificações defensivas
  const viewsTotal = payload?.find((p: any) => p?.dataKey === 'views_total')?.value || 0;
  const viewsUnique = payload?.find((p: any) => p?.dataKey === 'views_unique')?.value || 0;
  const playRate = payload?.find((p: any) => p?.dataKey === 'play_rate')?.value;

  // Mock tempo médio assistido (seria vindo do backend em produção)
  const avgWatchTime = Math.round(viewsTotal * 0.45) || 0;

  return (
    <div 
      className="bg-white rounded-[14px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-[#E4EBFF] px-5 py-4 min-w-[220px]"
      style={{ animation: 'tooltipFadeIn 120ms ease-out' }}
      role="tooltip"
      aria-live="polite"
    >
      {/* Data - PRD: título font-weight 600 */}
      <p className="text-[11px] font-semibold text-[#96A0B5] uppercase tracking-wide mb-3 pb-2 border-b border-[#F0F3F9]">
        {formattedDate}
      </p>
      
      {/* Métricas - PRD: ícones coloridos (azul, verde, laranja, roxo) */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#4773F7]" />
            <span className="text-[13px] text-[#6B7280] font-medium">{translateInterfaceText("Visualizações Totais")}</span>
          </div>
          <span className="text-[14px] font-semibold text-[#1E2330]">
            {viewsTotal.toLocaleString(getInterfaceLanguage())}
          </span>
        </div>
        
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#60A5FA]" />
            <span className="text-[13px] text-[#6B7280] font-medium">{translateInterfaceText("Visualizações Únicas")}</span>
          </div>
          <span className="text-[14px] font-semibold text-[#1E2330]">
            {viewsUnique.toLocaleString(getInterfaceLanguage())}
          </span>
        </div>
        
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#34D399]" />
            <span className="text-[13px] text-[#6B7280] font-medium">{translateInterfaceText("Play Rate do Dia")}</span>
          </div>
          <span className="text-[14px] font-semibold text-[#1E2330]">
            {playRate !== undefined ? `${(playRate * 100).toFixed(1)}%` : '--'}
          </span>
        </div>
        
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />
            <span className="text-[13px] text-[#6B7280] font-medium">{translateInterfaceText("Tempo Médio Assistido")}</span>
          </div>
          <span className="text-[14px] font-semibold text-[#1E2330]">
            {avgWatchTime}s
          </span>
        </div>
      </div>
    </div>
  );
};

// Dot customizado PRD: borda white 2px, fundo #4773F7
const PremiumDot = (props: any) => {
  useLanguage();
  const { cx, cy, dataKey } = props;
  
  if (!cx || !cy) return null;
  
  const color = dataKey === 'views_total' ? '#4773F7' : '#60A5FA';
  
  return (
    <g>
      {/* Glow externo sutil */}
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill={color}
        fillOpacity={0.08}
      />
      {/* Ponto principal - PRD: borda white 2px, fundo azul */}
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={color}
        stroke="white"
        strokeWidth={2}
        style={{ 
          filter: 'drop-shadow(0 0 3px rgba(71, 115, 247, 0.25))',
          cursor: 'pointer'
        }}
      />
    </g>
  );
};

// Active dot PRD: borda white 2px, fundo #4773F7
const PremiumActiveDot = (props: any) => {
  useLanguage();
  const { cx, cy, dataKey } = props;
  
  if (!cx || !cy) return null;
  
  const color = dataKey === 'views_total' ? '#4773F7' : '#60A5FA';
  
  return (
    <g>
      {/* Glow externo sutil */}
      <circle cx={cx} cy={cy} r={10} fill={color} fillOpacity={0.08} />
      <circle cx={cx} cy={cy} r={6} fill={color} fillOpacity={0.12} />
      {/* Ponto principal - PRD */}
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill={color}
        stroke="white"
        strokeWidth={2}
        style={{ 
          filter: 'drop-shadow(0 0 4px rgba(71, 115, 247, 0.3))',
          cursor: 'pointer'
        }}
      />
    </g>
  );
};

// Calcula intervalo para exibir exatamente 8 labels no eixo X
const calculateTickInterval = (dataLength: number): number => {
  if (dataLength <= 8) return 0; // Mostrar todas se 8 ou menos
  // Divide dados em 8 intervalos (7 espaços entre 8 labels)
  return Math.max(0, Math.floor((dataLength - 1) / 7));
};

export function ViewsChart({ data, isLoading }: ViewsChartProps) {
  useLanguage();
  const [showUnique, setShowUnique] = useState(false);
  const [showPlayRate, setShowPlayRate] = useState(false);
  if (isLoading) {
    return (
      <Card className="views-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-gradient-to-b from-white to-[#FAFAFA] shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
        <CardContent className="px-8 pt-8 pb-10">
          <div className="h-[400px] bg-gradient-to-b from-[#F3F6FF] to-white animate-pulse rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="views-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-gradient-to-b from-white to-[#FAFAFA] shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
        <CardContent className="p-12 flex items-center justify-center" style={{ minHeight: 480 }}>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EBF4FF] flex items-center justify-center">
              <LineChartIcon className="h-8 w-8 text-[#4B7BFF]" />
            </div>
            <h3 className="text-lg font-semibold text-[#2B3343] mb-2">
              {translateInterfaceText("Sem visualizações no período selecionado")}</h3>
            <p className="text-[#8A94A6] text-sm">
              {translateInterfaceText("Tente ampliar o período ou visualizar outro vídeo.")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calcular valor máximo para baseline
  const maxValue = Math.max(...data.map(d => (d && d.views_total) || 0));

  return (
    <Card className="views-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-white md:bg-gradient-to-b md:from-white md:to-[#FAFAFA] shadow-[0_4px_10px_rgba(0,0,0,0.06)] md:shadow-[0_6px_24px_rgba(0,0,0,0.06)] overflow-hidden">
      {/* Mobile: padding 12px, Desktop: padding 24-32px */}
      <CardContent className="px-3 py-4 md:px-8 md:pt-8 md:pb-10">
        {/* Título premium - Mobile: 16px, Desktop: 18px */}
        <h3 className="text-[16px] md:text-[18px] font-semibold text-[#2B3343] mb-3 md:mb-4">
          {translateInterfaceText("Desempenho diário")}</h3>
        
        {/* Header com botões badge premium - Mobile: flex wrap, gap menor */}
        <div className="mb-4 md:mb-6 flex items-center justify-end">
          <div className="flex items-center gap-1.5 md:gap-2">
            <button
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-[10px] text-[12px] md:text-[13px] font-medium border transition-all duration-200 ${
                showUnique 
                  ? 'bg-[#2F67FF] text-white border-[#2F67FF] shadow-[0_2px_8px_rgba(47,103,255,0.25)]' 
                  : 'bg-[#F0F4FF] text-[#5062A9] border-transparent hover:bg-[rgba(47,103,255,0.07)]'
              }`}
              onClick={() => setShowUnique(!showUnique)}
            >
              {translateInterfaceText("Únicas")}</button>
            <button
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-[10px] text-[12px] md:text-[13px] font-medium border transition-all duration-200 ${
                showPlayRate 
                  ? 'bg-[#2F67FF] text-white border-[#2F67FF] shadow-[0_2px_8px_rgba(47,103,255,0.25)]' 
                  : 'bg-[#F0F4FF] text-[#5062A9] border-transparent hover:bg-[rgba(47,103,255,0.07)]'
              }`}
              onClick={() => setShowPlayRate(!showPlayRate)}
            >
              {translateInterfaceText("Play Rate")}</button>
          </div>
        </div>

        {/* Container com fundo premium - Mobile: padding 12px, Desktop: 24px */}
        <div 
          className="views-chart-container rounded-[14px] overflow-hidden p-3 md:p-6"
          style={{ 
            background: 'linear-gradient(180deg, #F7F9FF 0%, #FFFFFF 100%)',
          }}
        >
          {/* Mobile: altura 320px, Desktop: 360px */}
          <ResponsiveContainer width="100%" height={320} className="md:!h-[360px]">
            <ComposedChart data={data} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
              <defs>
                {/* Gradiente para linha de views PRD: #4773F7 (3px) */}
                <linearGradient id="viewsLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4773F7" />
                  <stop offset="100%" stopColor="#4773F7" />
                </linearGradient>
                
                {/* Gradiente para área PRD: rgba(71,115,247,0.25) → rgba(71,115,247,0.02) */}
                <linearGradient id="viewsAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4773F7" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#4773F7" stopOpacity={0.02} />
                </linearGradient>
                
                {/* Gradiente para linha de unique */}
                <linearGradient id="uniqueLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#60a5fa" />
                </linearGradient>
                
                {/* Gradiente para área de unique */}
                <linearGradient id="uniqueAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.08} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                
                {/* Filter para glow na linha - Ultra sutil */}
                <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              {/* Grid Premium com azul sutil */}
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="rgba(75, 123, 255, 0.08)" 
                vertical={false}
              />
              
              {/* Baseline sutil na altura de 50% */}
              {maxValue > 0 && (
                <ReferenceLine 
                  yAxisId="left"
                  y={maxValue * 0.5}
                  stroke="rgba(75, 123, 255, 0.06)"
                  strokeDasharray="4 4"
                />
              )}
              
              {/* Eixo X Premium - Mobile: menos datas (intervalo maior), Desktop: todas */}
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => {
                  try {
                    return format(parseISO(date), 'dd/MM', { locale: getInterfaceDateLocale() });
                  } catch {
                    return date;
                  }
                }}
                axisLine={false}
                tickLine={false}
                tick={{ 
                  fill: '#8A94A6', 
                  fontSize: 11, 
                  fontWeight: 500
                }}
                dy={10}
                interval={data.length > 7 ? Math.floor(data.length / 5) : 0}
              />
              
              {/* Eixo Y Premium */}
              <YAxis 
                yAxisId="left"
                axisLine={{ stroke: '#E5E7EB', strokeWidth: 0.5 }}
                tickLine={false}
                tick={{ fill: '#96A0B5', fontSize: 12, fontWeight: 500 }}
                dx={-10}
              />
              
              {showPlayRate && (
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#96A0B5', fontSize: 12, fontWeight: 500 }}
                  tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                />
              )}
              
              {/* Tooltip Premium Apple-Like */}
              <Tooltip 
                content={<PremiumTooltip />} 
                cursor={{ 
                  stroke: '#3B82F6', 
                  strokeWidth: 1, 
                  strokeOpacity: 0.25,
                  strokeDasharray: '4 4'
                }}
              />
              
              {/* Área principal PRD: linha 3px #4773F7, gradient premium */}
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="views_total"
                  stroke="url(#viewsLineGradient)"
                  strokeWidth={1}
                fill="url(#viewsAreaGradient)"
                name="Visualizações Totais"
                dot={<PremiumDot dataKey="views_total" />}
                activeDot={<PremiumActiveDot dataKey="views_total" />}
                style={{ filter: 'drop-shadow(0 0 3px rgba(71, 115, 247, 0.2))' }}
              />
              
              {showUnique && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="views_unique"
                  stroke="url(#uniqueLineGradient)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#60A5FA', stroke: '#60A5FA' }}
                  activeDot={{ r: 5, fill: '#60A5FA', stroke: 'white', strokeWidth: 2 }}
                  name="Visualizações Únicas"
                />
              )}
              
              {showPlayRate && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="play_rate"
                  stroke="#34D399"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 3, fill: '#34D399', stroke: '#34D399' }}
                  activeDot={{ r: 5, fill: '#34D399', stroke: 'white', strokeWidth: 2 }}
                  name="Taxa de Play"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
